/* =====================================================
   OMNI STUDIO — main.js
   Nav · Reveals · Counters · FAQ · ROI · Year
   Pure vanilla JS. No external libs.
   Hooks Shopify theme editor section events so dynamic
   swaps keep working inside Customize.
   ===================================================== */

// Signal to CSS that JS is running — reveals only hide themselves once
// this class is present, so content stays visible if the script fails.
document.documentElement.classList.add('js-reveal');

const inDesignMode = document.documentElement.classList.contains('shopify-design-mode');

// Remove the retired global trial panel if a page is still serving a cached
// copy of the old footer section. The real footer CTA remains in place.
(function removeRetiredFooterTrialPanel() {
  function removePanels() {
    document.querySelectorAll('.r1f-trial-panel').forEach((panel) => panel.remove());
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', removePanels, { once: true });
  } else {
    removePanels();
  }
  document.addEventListener('shopify:section:load', removePanels);
})();

// ---- Nav (scrolled state + mobile toggle) ------------------------
function initNav(root) {
  const nav = (root || document).querySelector('.nav');
  if (!nav || nav.dataset.omniNavInit === '1') return;
  nav.dataset.omniNavInit = '1';

  let scrolled = false;
  function update() {
    const s = window.scrollY > 20;
    if (s !== scrolled) {
      scrolled = s;
      nav.classList.toggle('is-scrolled', s);
    }
  }
  update();
  window.addEventListener('scroll', update, { passive: true });

  const toggle = nav.querySelector('.nav-menu-btn, .nav__menu-btn');
  if (toggle) {
    toggle.addEventListener('click', () => {
      const open = nav.classList.toggle('is-open');
      toggle.setAttribute('aria-expanded', String(open));
      toggle.setAttribute('aria-label', open ? 'Close menu' : 'Open menu');
      document.body.style.overflow = open ? 'hidden' : '';
    });
  }

  nav.querySelectorAll('.nav-mobile a, .nav__mobile a').forEach((a) => {
    a.addEventListener('click', () => {
      nav.classList.remove('is-open');
      if (toggle) {
        toggle.setAttribute('aria-expanded', 'false');
        toggle.setAttribute('aria-label', 'Open menu');
      }
      document.body.style.overflow = '';
    });
  });
}

// Smooth anchor scroll — attached once document-wide
document.addEventListener('click', (e) => {
  const a = e.target.closest && e.target.closest('a[href^="#"]');
  if (!a) return;
  const href = a.getAttribute('href');
  if (!href || href === '#') return;
  const el = document.querySelector(href);
  if (el) {
    e.preventDefault();
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
});

// ---- Reveal on scroll (IntersectionObserver) ---------------------
function initReveals(root) {
  // In the Shopify editor, CSS forces reveals visible — no need to observe.
  if (inDesignMode) return;

  const scope = root || document;
  const els = scope.querySelectorAll('.reveal:not(.is-visible), .reveal-stagger:not(.is-visible)');
  if (!els.length) return;

  // Respect the OS reduced-motion preference: reveal everything immediately.
  const prefersReduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (prefersReduced || !window.IntersectionObserver) {
    els.forEach((el) => el.classList.add('is-visible'));
    return;
  }

  const io = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          io.unobserve(entry.target);
        }
      });
    },
    { threshold: 0.1, rootMargin: '0px 0px -4% 0px' }
  );

  els.forEach((el) => io.observe(el));

  // Safety net: any reveal still hidden after 4s gets force-revealed,
  // so users can never see a stuck-blank section if the observer misfires.
  setTimeout(() => {
    scope
      .querySelectorAll('.reveal:not(.is-visible), .reveal-stagger:not(.is-visible)')
      .forEach((el) => el.classList.add('is-visible'));
  }, 4000);
}

// ---- Number counter (data-count-to) ------------------------------
function initCounters(root) {
  const scope = root || document;
  const els = scope.querySelectorAll('[data-count-to]:not([data-count-done])');
  if (!els.length) return;

  // In the editor, write the final value immediately — skip animation.
  if (inDesignMode) {
    els.forEach((el) => {
      const to = parseFloat(el.dataset.countTo);
      const decimals = parseInt(el.dataset.decimals || '0', 10);
      const prefix = el.dataset.prefix || '';
      const suffix = el.dataset.suffix || '';
      el.textContent = prefix + to.toFixed(decimals).replace(/\B(?=(\d{3})+(?!\d))/g, ',') + suffix;
      el.setAttribute('data-count-done', '1');
    });
    return;
  }

  const io = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const el = entry.target;
        const to = parseFloat(el.dataset.countTo);
        const decimals = parseInt(el.dataset.decimals || '0', 10);
        const prefix = el.dataset.prefix || '';
        const suffix = el.dataset.suffix || '';
        const duration = parseInt(el.dataset.duration || '1400', 10);
        const start = performance.now();
        const format = (n) =>
          prefix + n.toFixed(decimals).replace(/\B(?=(\d{3})+(?!\d))/g, ',') + suffix;
        function tick(now) {
          const p = Math.min(1, (now - start) / duration);
          const eased = 1 - Math.pow(1 - p, 3);
          el.textContent = format(to * eased);
          if (p < 1) requestAnimationFrame(tick);
          else el.textContent = format(to);
        }
        requestAnimationFrame(tick);
        el.setAttribute('data-count-done', '1');
        io.unobserve(el);
      });
    },
    { threshold: 0.3 }
  );

  els.forEach((el) => {
    el.textContent = (el.dataset.prefix || '') + '0' + (el.dataset.suffix || '');
    io.observe(el);
  });
}

// ---- ROI calculator (pricing page) -------------------------------
function initROI(root) {
  const form = (root || document).querySelector('[data-roi]');
  if (!form || form.dataset.omniRoiInit === '1') return;
  form.dataset.omniRoiInit = '1';

  const calls = form.querySelector('#roi-calls');
  const ticket = form.querySelector('#roi-ticket');
  const out1 = form.querySelector('[data-roi-out="recovered"]');
  const out2 = form.querySelector('[data-roi-out="monthly"]');
  const out3 = form.querySelector('[data-roi-out="annual"]');
  const out4 = form.querySelector('[data-roi-out="roi"]');
  if (!calls || !ticket) return;

  const starter = 297;

  function format(n) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency', currency: 'USD', maximumFractionDigits: 0,
    }).format(n);
  }

  function compute() {
    const missed = Math.max(0, parseInt(calls.value, 10) || 0) * 4.33;
    const avg = Math.max(0, parseInt(ticket.value, 10) || 0);
    const recovered = missed * 0.82 * 0.68;
    const monthly = recovered * avg;
    const annual = monthly * 12;
    const roi = monthly > 0 ? Math.round(((monthly - starter) / starter) * 100) : 0;

    if (out1) out1.textContent = recovered.toFixed(0) + ' jobs';
    if (out2) out2.textContent = format(monthly);
    if (out3) out3.textContent = format(annual);
    if (out4) out4.textContent = (roi >= 0 ? '+' : '') + roi + '%';
  }

  form.addEventListener('input', compute);
  compute();
}

// ---- FAQ accordion -----------------------------------------------
function initFAQ(root) {
  (root || document).querySelectorAll('.faq__item').forEach((item) => {
    if (item.dataset.omniFaqInit === '1') return;
    item.dataset.omniFaqInit = '1';
    const btn = item.querySelector('.faq__q');
    if (!btn) return;
    btn.addEventListener('click', () => {
      const open = item.classList.toggle('is-open');
      btn.setAttribute('aria-expanded', String(open));
    });
  });
}

// ---- Year in footer ----------------------------------------------
function initYear(root) {
  (root || document).querySelectorAll('#year').forEach((el) => {
    el.textContent = new Date().getFullYear();
  });
}

// ---- Boot --------------------------------------------------------
function bootAll(root) {
  initNav(root);
  initReveals(root);
  initCounters(root);
  initROI(root);
  initFAQ(root);
  initYear(root);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => bootAll(document));
} else {
  bootAll(document);
}

// ---- Shopify theme editor hooks ----------------------------------
// Re-init when merchants add/reorder sections or blocks.
if (window.Shopify && (window.Shopify.designMode || inDesignMode)) {
  document.addEventListener('shopify:section:load', (e) => bootAll(e.target));
  document.addEventListener('shopify:section:select', (e) => bootAll(e.target));
  document.addEventListener('shopify:block:select', (e) => bootAll(e.target));
}
/* omni-ga4-cta-event-listener-main-fallback:2026-06-17 */
/* Omni Studio GA4 CTA event listener. Approved Shopify theme install: 2026-06-17. */
(function () {
  if (window.__omniGa4CtaListenerInstalled) return;
  window.__omniGa4CtaListenerInstalled = true;

  var KEY_EVENT_NAMES = {
    booking_intent: 'omni_book_strategy_click',
    contact_intent: 'omni_contact_click',
    pricing_intent: 'omni_pricing_click',
    audit_request_intent: 'omni_audit_request_click'
  };

  var SUPPORTING_EVENT_NAMES = {
    managed_ops_intent: 'omni_managed_ops_click',
    proof_intent: 'omni_case_study_click',
    blog_money_route_intent: 'omni_blog_money_route_click'
  };

  function normalizeSpace(value) {
    return String(value || '').replace(/\s+/g, ' ').trim();
  }

  function safeText(value) {
    return normalizeSpace(value).slice(0, 80);
  }

  function safeUrl(rawUrl) {
    try {
      var url = new URL(rawUrl, window.location.href);
      if (!/^https?:$/.test(url.protocol)) return '';
      return url.origin + url.pathname + url.hash;
    } catch (error) {
      return '';
    }
  }

  function pathOf(rawUrl) {
    try {
      return new URL(rawUrl, window.location.href).pathname.toLowerCase();
    } catch (error) {
      return '';
    }
  }

  function isBlogMoneyRoute(destinationPath) {
    return [
      '/pages/contact',
      '/pages/pricing',
      '/pages/ai-automation-audit',
      '/pages/managed-ai-ops',
      '/pages/case-studies'
    ].some(function (targetPath) {
      return destinationPath === targetPath || destinationPath.indexOf(targetPath + '/') === 0;
    });
  }

  function classifyOmniCta(linkText, linkHref, pagePath) {
    var text = String(linkText || '').toLowerCase();
    var href = String(linkHref || '').toLowerCase();
    var haystack = text + ' ' + href;
    var destinationPath = pathOf(linkHref);

    if (haystack.indexOf('book') >= 0 || haystack.indexOf('strategy') >= 0 || haystack.indexOf('call') >= 0 || haystack.indexOf('demo') >= 0) {
      return 'booking_intent';
    }
    if (haystack.indexOf('audit') >= 0 || destinationPath === '/pages/ai-automation-audit') {
      return 'audit_request_intent';
    }
    if (haystack.indexOf('pricing') >= 0 || haystack.indexOf('price') >= 0 || destinationPath === '/pages/pricing') {
      return 'pricing_intent';
    }
    if (haystack.indexOf('mailto:') >= 0 || haystack.indexOf('contact') >= 0 || destinationPath === '/pages/contact') {
      return 'contact_intent';
    }
    if (haystack.indexOf('managed-ai-ops') >= 0 || haystack.indexOf('managed ai') >= 0 || destinationPath === '/pages/managed-ai-ops') {
      return 'managed_ops_intent';
    }
    if (haystack.indexOf('case-stud') >= 0 || haystack.indexOf('customer') >= 0 || destinationPath === '/pages/case-studies') {
      return 'proof_intent';
    }
    if (String(pagePath || '').indexOf('/blogs/') === 0 && isBlogMoneyRoute(destinationPath)) {
      return 'blog_money_route_intent';
    }
    return '';
  }

  function sourceSection(anchor) {
    if (!anchor || !anchor.closest) return 'unknown';
    if (anchor.closest('header,[role="banner"],.site-header')) return 'header';
    if (anchor.closest('footer,[role="contentinfo"],.site-footer')) return 'footer';
    if (anchor.closest('[class*="hero"],[id*="hero"]')) return 'hero';
    return 'body';
  }

  function buildEvent(anchor) {
    if (!anchor) return null;
    var href = anchor && anchor.getAttribute ? anchor.getAttribute('href') : '';
    var linkUrl = safeUrl(href);
    if (!linkUrl) return null;

    var pagePath = window.location.pathname || '';
    var linkText = safeText(anchor.innerText || anchor.textContent || anchor.getAttribute('aria-label') || '');
    var intent = classifyOmniCta(linkText, linkUrl, pagePath);
    var eventName = KEY_EVENT_NAMES[intent] || SUPPORTING_EVENT_NAMES[intent] || '';
    if (!eventName) return null;

    return {
      eventName: eventName,
      params: {
        link_url: linkUrl,
        link_text: linkText,
        page_path: pagePath,
        page_title: safeText(document.title || ''),
        cta_intent: intent,
        source_section: sourceSection(anchor)
      }
    };
  }

  document.addEventListener('click', function (event) {
    var anchor = event.target && event.target.closest ? event.target.closest('a[href]') : null;
    var builtEvent = buildEvent(anchor);
    if (!builtEvent) return;

    if (typeof window.gtag === 'function') {
      window.gtag('event', builtEvent.eventName, builtEvent.params);
    }
  }, true);

  window.__omniGa4Cta = {
    classifyOmniCta: classifyOmniCta,
    buildEvent: buildEvent
  };
})();
/* /omni-ga4-cta-event-listener-main-fallback:2026-06-17 */

/* /omni-legacy-trial-bridge-runtime-repair:2026-08-03 */
(function () {
  if (window.__omniLegacyTrialBridgeAssetRepair) return;
  window.__omniLegacyTrialBridgeAssetRepair = true;

  var marker = String.fromCharCode(123, 37) + " render 'omni-industry-trial-bridge'";
  var styleId = 'omni-legacy-trial-bridge-runtime-style';
  var bannerMarkup = '' +
    '<section class="omni-legacy-trial-bridge-wrap" aria-label="Try your business on Omni">' +
      '<div class="omni-legacy-trial-bridge" data-omni-industry-trial-bridge data-omni-industry="Restaurants &amp; Hospitality">' +
        '<div class="omni-legacy-trial-bridge-inner">' +
          '<div>' +
            '<p class="omni-legacy-trial-bridge-kicker"><i aria-hidden="true"></i>14-day free trial · Restaurants &amp; Hospitality</p>' +
            '<h2>Try your business on Omni — free for 14 days.</h2>' +
            '<p class="omni-legacy-trial-bridge-copy">Bring your real services, prices, estimate logic, and approval rules. See how Omni fits the way your team works before you decide.</p>' +
            '<div class="omni-legacy-trial-bridge-proof" aria-label="Trial benefits">' +
              '<span><b aria-hidden="true">✓</b>No credit card</span>' +
              '<span><b aria-hidden="true">✓</b>Your real data</span>' +
              '<span><b aria-hidden="true">✓</b>Human-backed setup</span>' +
            '</div>' +
          '</div>' +
          '<div class="omni-legacy-trial-bridge-actions">' +
            '<p class="omni-legacy-trial-bridge-actions-label">Start with the workflow you need most</p>' +
            '<a class="omni-legacy-trial-bridge-primary" href="/pages/trial" data-omni-trial-open data-omni-trial-source="restaurants-midpage" data-omni-trial-industry="Restaurants &amp; Hospitality">Try Omni free for 14 days <span aria-hidden="true">&nbsp;→</span></a>' +
            '<a class="omni-legacy-trial-bridge-human" href="https://calendar.app.google/RWP8aKwxXQ9itunk8" target="_blank" rel="noopener" data-omni-human-cta>Talk to a human</a>' +
            '<p class="omni-legacy-trial-bridge-note">We’ll help you map the first workflow around your business.</p>' +
          '</div>' +
        '</div>' +
      '</div>' +
    '</section>';

  function addStyles() {
    if (document.getElementById(styleId)) return;
    var style = document.createElement('style');
    style.id = styleId;
    style.textContent = '' +
      '.omni-legacy-trial-bridge-wrap{display:block;width:min(920px,calc(100% - 32px));max-width:100%;min-width:0;margin:clamp(48px,6vw,78px) auto;box-sizing:border-box;overflow-x:clip}' +
      '.omni-legacy-trial-bridge{position:relative;isolation:isolate;width:100%;max-width:100%;min-width:0;overflow:hidden;border:1px solid rgba(255,255,255,.12);border-radius:18px;background:#15191E;color:#fff;box-shadow:0 20px 54px rgba(21,25,30,.16)}' +
      '.omni-legacy-trial-bridge::before{content:"";position:absolute;z-index:-1;width:260px;height:180px;right:-120px;top:-115px;border-radius:50%;background:rgba(255,89,0,.18);filter:blur(16px)}' +
      '.omni-legacy-trial-bridge::after{content:"";position:absolute;z-index:-1;width:220px;height:130px;left:36%;bottom:-110px;border-radius:50%;background:rgba(117,195,187,.10);filter:blur(22px)}' +
      '.omni-legacy-trial-bridge-inner{display:grid;grid-template-columns:minmax(0,1fr) minmax(210px,.45fr);gap:clamp(20px,4vw,42px);align-items:center;width:100%;max-width:100%;min-width:0;padding:22px 24px}' +
      '.omni-legacy-trial-bridge-inner>div{min-width:0}' +
      '.omni-legacy-trial-bridge-kicker{display:flex;align-items:center;gap:8px;margin:0 0 8px;color:#FFB078;font:700 10px/1.2 ui-monospace,"DM Mono",monospace;letter-spacing:.14em;text-transform:uppercase}' +
      '.omni-legacy-trial-bridge-kicker i{display:block;width:7px;height:7px;border-radius:50%;background:#FF5900;box-shadow:0 0 0 4px rgba(255,89,0,.16)}' +
      '.omni-legacy-trial-bridge h2{max-width:560px;margin:0;color:#fff;font:800 clamp(28px,3.2vw,44px)/.98 "Plus Jakarta Sans",system-ui,sans-serif;letter-spacing:-.05em}' +
      '.omni-legacy-trial-bridge-copy{max-width:560px;margin:10px 0 0;color:rgba(255,255,255,.68);font:400 13px/1.45 Inter,system-ui,sans-serif}' +
      '.omni-legacy-trial-bridge-proof{display:flex;flex-wrap:wrap;gap:7px 14px;margin:12px 0 0;color:rgba(255,255,255,.76);font:600 10.5px/1.35 Inter,system-ui,sans-serif}' +
      '.omni-legacy-trial-bridge-proof span{display:inline-flex;align-items:center;gap:7px}' +
      '.omni-legacy-trial-bridge-proof b{display:grid;place-items:center;width:16px;height:16px;border-radius:50%;background:#CFE8C7;color:#1B3F26;font-size:10px}' +
      '.omni-legacy-trial-bridge-actions{display:flex;flex-direction:column;align-items:stretch;gap:7px;width:100%;min-width:0;padding:12px;border:1px solid rgba(255,255,255,.13);border-radius:13px;background:rgba(255,255,255,.07);backdrop-filter:blur(12px);-webkit-backdrop-filter:blur(12px)}' +
      '.omni-legacy-trial-bridge-actions-label{margin:0 0 1px;color:rgba(255,255,255,.56);font:600 9px/1.3 ui-monospace,"DM Mono",monospace;letter-spacing:.1em;text-transform:uppercase}' +
      '.omni-legacy-trial-bridge-actions a{display:inline-flex;align-items:center;justify-content:center;width:100%;min-width:0;min-height:36px;padding:0 12px;border-radius:9px;font:800 12px/1 Inter,system-ui,sans-serif;text-align:center;text-decoration:none;overflow-wrap:anywhere;transition:transform .2s ease,background .2s ease,box-shadow .2s ease}' +
      '.omni-legacy-trial-bridge-actions a:hover{transform:translateY(-2px)}' +
      '.omni-legacy-trial-bridge-actions a:focus-visible{outline:2px solid #FFB078;outline-offset:3px}' +
      '.omni-legacy-trial-bridge-primary{background:#FF5900;color:#fff;box-shadow:0 12px 26px rgba(255,89,0,.24)}' +
      '.omni-legacy-trial-bridge-primary:hover{background:#C24400;box-shadow:0 16px 32px rgba(255,89,0,.34)}' +
      '.omni-legacy-trial-bridge-human{border:1px solid rgba(255,255,255,.2);background:rgba(255,255,255,.06);color:#fff}' +
      '.omni-legacy-trial-bridge-human:hover{background:rgba(255,255,255,.13)}' +
      '.omni-legacy-trial-bridge-note{margin:2px 0 0;color:rgba(255,255,255,.48);font:400 10px/1.35 Inter,system-ui,sans-serif;text-align:center}' +
      '@media(max-width:760px){.omni-legacy-trial-bridge-wrap{width:min(100%,calc(100% - 32px));margin:42px auto 60px}.omni-legacy-trial-bridge-inner{grid-template-columns:minmax(0,1fr);gap:20px;padding:22px 18px}.omni-legacy-trial-bridge h2{font-size:clamp(30px,9vw,42px)}.omni-legacy-trial-bridge-copy{font-size:14px}.omni-legacy-trial-bridge-proof{gap:8px 12px;font-size:10.5px}.omni-legacy-trial-bridge-actions{padding:12px}.omni-legacy-trial-bridge-actions a{width:100%;max-width:100%;white-space:normal;line-height:1.25}}';
    document.head.appendChild(style);
  }

  function repair(root) {
    if (!root) return;
    var walker = document.createTreeWalker(root, window.NodeFilter ? NodeFilter.SHOW_TEXT : 4);
    var matches = [];
    while (walker.nextNode()) {
      if ((walker.currentNode.nodeValue || '').indexOf(marker) !== -1) matches.push(walker.currentNode);
    }
    if (!matches.length) return;
    addStyles();
    matches.forEach(function (node) {
      var replacement = document.createElement('div');
      replacement.innerHTML = bannerMarkup;
      if (node.parentNode) node.parentNode.replaceChild(replacement.firstElementChild, node);
    });
  }

  function run() {
    repair(document.body);
    if (document.body && 'MutationObserver' in window) {
      new MutationObserver(function (records, observer) {
        repair(document.body);
        if (document.querySelector('[data-omni-industry-trial-bridge]')) observer.disconnect();
      }).observe(document.body, { childList: true, subtree: true });
    }
  }

  if (document.body) run();
  else document.addEventListener('DOMContentLoaded', run, { once: true });
})();
